/**
 * Unified Typography System
 *
 * Single source of truth for typography across the application.
 * Fixes dual font scale issue (xs: 10 vs 11, sm: 12 vs 13).
 *
 * @packageDocumentation
 */

// ============================================================================
// Font Sizes (Single Unified Scale)
// ============================================================================

/**
 * Font size scale in pixels.
 * Minimum size increased to 12px for WCAG 2.1 Level AA compliance.
 */
export const fontSize = {
  xs: 12,      // Minimum readable (WCAG compliant)
  sm: 14,      // Body text minimum, mobile friendly
  base: 16,    // Standard body text
  lg: 18,      // Large body / small headings
  xl: 24,      // Section headings
  '2xl': 32,   // Large headings
  '3xl': 48,   // Hero/display text
  '4xl': 64,   // Extra large display
} as const

// ============================================================================
// Font Weights
// ============================================================================

export const fontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  black: 900,
} as const

// ============================================================================
// Line Heights
// ============================================================================

export const lineHeight = {
  tight: 1.2,    // Headings, compact text
  snug: 1.3,     // Subheadings
  normal: 1.5,   // Body text (optimal readability)
  relaxed: 1.75, // Loose body text
  loose: 2,      // Very spacious
} as const

// ============================================================================
// Letter Spacing
// ============================================================================

export const letterSpacing = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
} as const

// ============================================================================
// Font Families
// ============================================================================

export const fontFamily = {
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  display: '"Space Grotesk", "Inter", sans-serif',
  mono: '"Consolas", "Monaco", "Courier New", monospace',
} as const

// ============================================================================
// Semantic Text Styles
// ============================================================================

/**
 * Pre-configured text styles for common use cases.
 * Use these for consistent typography across components.
 */
export const textStyles = {
  // Display text (hero sections)
  display: {
    fontSize: fontSize['4xl'],
    fontWeight: fontWeight.black,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.tight,
    fontFamily: fontFamily.display,
  },

  // Headings
  h1: {
    fontSize: fontSize['3xl'],
    fontWeight: fontWeight.bold,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.tight,
    fontFamily: fontFamily.display,
  },
  h2: {
    fontSize: fontSize['2xl'],
    fontWeight: fontWeight.bold,
    lineHeight: lineHeight.tight,
    fontFamily: fontFamily.display,
  },
  h3: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.snug,
    fontFamily: fontFamily.display,
  },
  h4: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.snug,
  },

  // Body text
  body: {
    fontSize: fontSize.base,
    fontWeight: fontWeight.normal,
    lineHeight: lineHeight.normal,
    fontFamily: fontFamily.sans,
  },
  bodyLarge: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.normal,
    lineHeight: lineHeight.normal,
    fontFamily: fontFamily.sans,
  },
  bodySmall: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.normal,
    lineHeight: lineHeight.normal,
    fontFamily: fontFamily.sans,
  },

  // UI elements
  caption: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.normal,
    lineHeight: lineHeight.snug,
  },
  label: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.wide,
  },
  button: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.wide,
  },

  // Code/monospace
  code: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.normal,
    lineHeight: lineHeight.normal,
    fontFamily: fontFamily.mono,
  },
} as const

// ============================================================================
// Responsive Typography
// ============================================================================

/**
 * Font size scale multipliers for different breakpoints.
 * Apply these to base font sizes for responsive scaling.
 */
export const responsiveScale = {
  mobile: 0.92,    // 92% of base (increased from 0.85 for better mobile readability)
  tablet: 1.0,     // Base size
  desktop: 1.05,   // 5% larger
  wide: 1.1,       // 10% larger for large displays
} as const

// ============================================================================
// Exports
// ============================================================================

export const typography = {
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  fontFamily,
  textStyles,
  responsiveScale,
} as const
